import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { ThemeProvider, createTheme } from "@mui/material";
import type { Theme } from "@mui/material";
import { getTextGradient, applyGradientText } from "../constants/gradient";
import type { PaletteOptions } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";

interface ThemeContextValue {
  mode: "light" | "dark";
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useThemeMode = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeContextProvider");
  }
  return context;
};

interface ThemeContextProviderProps {
  children: ReactNode;
}

export const ThemeContextProvider = ({
  children,
}: ThemeContextProviderProps): JSX.Element => {
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem("themeMode");
    const initialMode = saved === "light" || saved === "dark" ? saved : "dark";
    // Initialize data attribute on body for CSS selectors
    if (typeof document !== "undefined") {
      document.body.setAttribute("data-theme", initialMode);
    }
    return initialMode;
  });

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", next); // Save to localStorage
      return next;
    });
  };

  // Update localStorage and data attribute whenever mode changes
  useEffect(() => {
    localStorage.setItem("themeMode", mode);
    // Set data attribute on body for CSS selectors (e.g., Toast styling)
    document.body.setAttribute("data-theme", mode);
  }, [mode]);

  const theme = useMemo(() => {
    const palette: PaletteOptions =
      mode === "light"
        ? ({
            mode: "light",
            primary: {
              main: "#3f51b5",
              light: "#6573c3",
              dark: "#2c387e",
              contrastText: "#ffffff",
            },
            secondary: {
              main: "#f50057",
              light: "#f73378",
              dark: "#ab003c",
              contrastText: "#ffffff",
            },
            background: {
              default: "#fafafa",
              paper: "#ffffff",
            },
            text: {
              primary: "#1a1a1a",
              secondary: "#555",
            },
            divider: "#e0e0e0",
            error: {
              main: "#f44336",
            },
            success: {
              main: "#4caf50",
            },
            warning: {
              main: "#ff9800",
            },
            info: {
              main: "#2196f3",
            },
          } as const)
        : ({
            mode: "dark",
            primary: {
              main: "#90caf9",
              light: "#e3f2fd",
              dark: "#42a5f5",
              contrastText: "#000000",
            },
            secondary: {
              main: "#f48fb1",
              light: "#f8bbd0",
              dark: "#c2185b",
              contrastText: "#000000",
            },
            background: {
              default: "#121212",
              paper: "#1e1e1e",
            },
            text: {
              primary: "#ffffff",
              secondary: "#cccccc",
            },
            divider: "#333",
            error: {
              main: "#ef5350",
            },
            success: {
              main: "#66bb6a",
            },
            warning: {
              main: "#ffa726",
            },
            info: {
              main: "#29b6f6",
            },
          } as const);

    const baseTheme = createTheme({
      palette,
      typography: {
        fontFamily: `"Manrope", "Roboto", "Helvetica", "Arial", sans-serif`,
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            html: {
              height: "100%",
            },
            body: {
              height: "100%",
              margin: 0,
              padding: 0,
              fontFamily: `"Manrope", "Roboto", "Helvetica", "Arial", sans-serif`,
              backgroundColor:
                (palette.background && palette.background.default) || undefined,
              color: (palette.text && palette.text.primary) || undefined,
            },
            "#root": {
              height: "100%",
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: "none",
              fontWeight: 600,
            },
          },
        },
      },
    });
    // Augment theme with customGradients and mixins for convenience
    const themeWithCustom = baseTheme as any;
    themeWithCustom.customGradients = { text: getTextGradient(baseTheme) };
    themeWithCustom.mixins = {
      ...(baseTheme as any).mixins,
      gradientText: () => applyGradientText(baseTheme),
    };
    return themeWithCustom as Theme;
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
